/**
 * Générateur — Attendu 658 — Définition de la médiatrice d'un segment.
 * Niveau : 6ème | Cluster : 31 | Domaine : Espace et géométrie
 *
 * 5 exercices / 5 formats :
 *   - mediatrice_vf_reconnaissance     : V/F (3V+3F stricte) + figure SVG
 *                                         (segment [AB] + 1 droite), 4 régimes
 *                                         géométriques M / P / L / N.
 *   - mediatrice_qcm_identifier_figure : QCM 4 choix + figure SVG (segment +
 *                                         4 droites nommées (d₁)..(d₄)),
 *                                         l'élève identifie la médiatrice.
 *   - mediatrice_qcm_definition        : QCM 4 choix (définition verbale,
 *                                         2 formulations correctes + 3 idées
 *                                         fausses tirées parmi 6). Sans figure.
 *   - mediatrice_vocabulaire_trous     : texte à trous (drag), 6 modèles de
 *                                         phrases, un distracteur compatible
 *                                         garanti par trou.
 *   - mediatrice_notations_vf          : V/F (3V+3F) sur équivalences
 *                                         symétrie ↔ médiatrice, sans figure.
 *
 * Variété cible : ≥ 20 variantes uniques par format (contrainte auditGen).
 * Voir docs/fiches/FICHE_658.md et docs/fiches/PAYLOADS_658.md.
 */

import {
    randInt, randomChoice, randomChoiceMultiple, shuffle, pickLettresSains,
    makeQcm, makeVraiFaux, makeTexteTrou,
    subscriptToSvgTspan,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 658;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_VF_RECO = 'Reconnaissance médiatrice (V/F figure)';
const L_QCM_IDENT = 'Identifier la médiatrice (QCM figure)';
const L_QCM_DEF = 'Définition verbale (QCM)';
const L_VOCAB = 'Vocabulaire médiatrice (texte à trous)';
const L_NOTATIONS = 'Notations équivalentes (V/F)';

// Lettres valides — exclut I et O (ambiguïté avec 1, 0). Identique cluster 30.
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('');

// Pool de noms de droite (cluster 31)
const DROITES = ['(d)', '(Δ)', '(d₁)', '(ℓ)'];

// ViewBox commune Ex. 1 et Ex. 2
const VIEWBOX = { w: 300, h: 220 };
const CENTER = { x: 150, y: 110 };

const ORIENTATIONS_DEG = [0, 30, 45, 60, 90, 120, 135, 150];

// Couleurs des droites candidates (Ex. 2) — affectées par POSITION après le
// shuffle, donc sans corrélation avec la nature de la droite. Le segment [AB]
// et ses extrémités restent en noir.
const COULEURS_DROITES = ['#1D4ED8', '#EA580C', '#9333EA', '#15803D'];

// ═══════════════════════════════════════════════════════════
// HELPERS COMMUNS
// ═══════════════════════════════════════════════════════════

/** Encode une chaîne SVG en base64, compatible navigateur + Node. */
const svgToBase64 = (svg) => {
    if (typeof btoa === 'function') {
        try { return btoa(unescape(encodeURIComponent(svg))); } catch (_e) { /* fallthrough */ }
    }
    return Buffer.from(svg, 'utf-8').toString('base64');
};

/** Tire n lettres distinctes dans un pool (par défaut LETTRES). */
const pickN = (n, pool = LETTRES) => pickLettresSains(pool, n);

/**
 * Dessine le petit carré d'angle droit (L ouvert) à l'intersection de deux
 * directions. Forme : polyline ouverte en 3 points (P1 → P2 → P3).
 *
 * (cx, cy) = sommet de l'angle droit
 * (ux, uy) = vecteur unitaire (direction « horizontale » du L)
 * (vx, vy) = vecteur unitaire perpendiculaire (direction « verticale » du L)
 * size     = côté du L en px
 */
const drawRightAngleMarker = (cx, cy, ux, uy, vx, vy, size = 8) => {
    const p1x = cx + ux * size, p1y = cy + uy * size;
    const p2x = p1x + vx * size, p2y = p1y + vy * size;
    const p3x = cx + vx * size, p3y = cy + vy * size;
    return `<polyline points="${p1x.toFixed(1)},${p1y.toFixed(1)} ${p2x.toFixed(1)},${p2y.toFixed(1)} ${p3x.toFixed(1)},${p3y.toFixed(1)}" fill="none" stroke="#000" stroke-width="1.2" />`;
};

/**
 * Dessine une marque de milieu (tiret centré, perpendiculaire au segment).
 * (mx, my) = point à marquer, (nx, ny) = vecteur unitaire normal au segment,
 * len = longueur totale du tiret en px.
 */
const drawMidpointMark = (mx, my, nx, ny, len = 5) => {
    const x1 = mx - nx * len / 2, y1 = my - ny * len / 2;
    const x2 = mx + nx * len / 2, y2 = my + ny * len / 2;
    return `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="#000" stroke-width="1.2" stroke-linecap="round" />`;
};

/**
 * Étend une droite (passant par (px,py), de direction (dx,dy)) aux bords du
 * viewBox 300×220. Retourne {x1,y1,x2,y2} — les 2 intersections extrêmes avec
 * les 4 côtés du viewBox.
 */
const extendLineToViewBox = (px, py, dx, dy) => {
    const W = VIEWBOX.w, H = VIEWBOX.h;
    const ts = [];
    if (Math.abs(dx) > 1e-9) {
        ts.push((0 - px) / dx);
        ts.push((W - px) / dx);
    }
    if (Math.abs(dy) > 1e-9) {
        ts.push((0 - py) / dy);
        ts.push((H - py) / dy);
    }
    const pts = ts
        .map(t => ({ t, x: px + t * dx, y: py + t * dy }))
        .filter(p => p.x >= -0.5 && p.x <= W + 0.5 && p.y >= -0.5 && p.y <= H + 0.5)
        .sort((a, b) => a.t - b.t);
    if (pts.length < 2) {
        // Fallback horizontal passant par le centre
        return { x1: 0, y1: py, x2: W, y2: py };
    }
    return { x1: pts[0].x, y1: pts[0].y, x2: pts[pts.length - 1].x, y2: pts[pts.length - 1].y };
};

// ═══════════════════════════════════════════════════════════
// Ex. 1 — Reconnaissance « médiatrice ou non ? » (V/F + figure)
// ═══════════════════════════════════════════════════════════

/** 4 régimes géométriques : M, P, L, N. */
const REGIMES = ['M', 'P', 'L', 'N'];

/**
 * Pool de 12 affirmations atomiques. Chaque entrée a un classifieur `isTrue`
 * qui renvoie V/F selon le régime. Permet de garantir ≥ 3V et ≥ 3F pour
 * chacun des 4 régimes.
 */
const POOL_EX1 = [
    { text: (A, B, d) => `La droite ${d} est perpendiculaire au segment [${A}${B}].`,                               isTrue: (r) => r === 'M' || r === 'P' },
    { text: (A, B, d) => `La droite ${d} passe par le milieu du segment [${A}${B}].`,                               isTrue: (r) => r === 'M' || r === 'L' },
    { text: (A, B, d) => `La droite ${d} est la médiatrice du segment [${A}${B}].`,                                 isTrue: (r) => r === 'M' },
    { text: (A, B, d) => `La droite ${d} est un axe de symétrie du segment [${A}${B}].`,                            isTrue: (r) => r === 'M' },
    { text: (A, B, d) => `Les points ${A} et ${B} sont symétriques par rapport à la droite ${d}.`,                  isTrue: (r) => r === 'M' },
    { text: (A, B, d) => `${B} est le symétrique de ${A} par rapport à la droite ${d}.`,                            isTrue: (r) => r === 'M' },
    { text: (A, B, d) => `La droite ${d} n'est pas perpendiculaire au segment [${A}${B}].`,                         isTrue: (r) => r === 'L' || r === 'N' },
    { text: (A, B, d) => `La droite ${d} ne passe pas par le milieu du segment [${A}${B}].`,                        isTrue: (r) => r === 'P' || r === 'N' },
    { text: (A, B, d) => `La droite ${d} n'est pas la médiatrice du segment [${A}${B}].`,                           isTrue: (r) => r !== 'M' },
    { text: (A, B, d) => `La droite ${d} n'est pas un axe de symétrie du segment [${A}${B}].`,                      isTrue: (r) => r !== 'M' },
    { text: (A, B, d) => `Les points ${A} et ${B} ne sont pas symétriques par rapport à la droite ${d}.`,           isTrue: (r) => r !== 'M' },
    { text: (A, B, d) => `La droite ${d} ne coupe pas le segment [${A}${B}] en son milieu.`,                        isTrue: (r) => r === 'P' || r === 'N' },
];

/**
 * Construit le SVG figure Ex. 1 : segment [AB] + 1 droite (d), codage
 * médiatrice si régime M.
 */
const buildFigureEx1Svg = (Ax, Ay, Bx, By, A_name, B_name, d_name, regime, thetaRad) => {
    const Cx = CENTER.x, Cy = CENTER.y;
    const ux = Math.cos(thetaRad);
    const uy = Math.sin(thetaRad);
    const vx = -uy;
    const vy = ux;

    // Milieu I = centre de la viewBox (le segment est centré par construction)
    const Ix = Cx, Iy = Cy;

    // Construction de la droite (d) selon le régime
    let origineX = Ix, origineY = Iy, dirX = vx, dirY = vy;
    const delta = randInt(25, 35);
    const signDelta = randomChoice([-1, 1]);
    if (regime === 'P') {
        origineX = Ix + signDelta * delta * ux;
        origineY = Iy + signDelta * delta * uy;
    } else if (regime === 'L') {
        const alphaDeg = randomChoice([20, 25, 30, 45]) * randomChoice([-1, 1]);
        const alphaRad = alphaDeg * Math.PI / 180;
        dirX = Math.cos(alphaRad) * vx - Math.sin(alphaRad) * vy;
        dirY = Math.sin(alphaRad) * vx + Math.cos(alphaRad) * vy;
    } else if (regime === 'N') {
        const alphaDeg = randomChoice([20, 30, 45]) * randomChoice([-1, 1]);
        const alphaRad = alphaDeg * Math.PI / 180;
        origineX = Ix + signDelta * delta * ux;
        origineY = Iy + signDelta * delta * uy;
        dirX = Math.cos(alphaRad) * vx - Math.sin(alphaRad) * vy;
        dirY = Math.sin(alphaRad) * vx + Math.cos(alphaRad) * vy;
    }

    const { x1, y1, x2, y2 } = extendLineToViewBox(origineX, origineY, dirX, dirY);

    // Positions labels A et B : décalées de 12 px radialement + 6 px perpendic.
    const aLabelX = Ax - ux * 12 - vx * 6;
    const aLabelY = Ay - uy * 12 - vy * 6 + 4;
    const bLabelX = Bx + ux * 12 + vx * 6;
    const bLabelY = By + uy * 12 + vy * 6 + 4;

    // Position label droite : près de l'extrémité du haut (ou la plus à droite)
    const useFirst = (y1 <= y2);
    const labelBaseX = useFirst ? x1 : x2;
    const labelBaseY = useFirst ? y1 : y2;
    const dLabelX = Math.max(4, Math.min(VIEWBOX.w - 30, labelBaseX + 4));
    const dLabelY = Math.max(14, Math.min(VIEWBOX.h - 4, labelBaseY + 14));

    // ── Codage de la figure ────────────────────────────────────────────────
    // Les marques de milieu codent « I est le milieu de [AB] » : c'est une
    // propriété du SEGMENT, vraie dans les 4 régimes — donc toujours tracées.
    // Le carré d'angle droit code la perpendicularité : il n'apparaît que là où
    // la droite l'est réellement (régimes M et P), à son intersection avec [AB].
    // Ainsi la seule présence d'un codage ne trahit plus le régime.
    const TICK_OFFSET = 15;
    let codageSvg = '';
    codageSvg += drawMidpointMark(Ix - ux * TICK_OFFSET, Iy - uy * TICK_OFFSET, vx, vy, 6);
    codageSvg += drawMidpointMark(Ix + ux * TICK_OFFSET, Iy + uy * TICK_OFFSET, vx, vy, 6);
    if (regime === 'M' || regime === 'P') {
        // L ouvert vers l'extérieur du segment, pour ne pas recouvrir les marques
        const signU = (regime === 'P') ? signDelta : randomChoice([-1, 1]);
        const signV = randomChoice([-1, 1]);
        codageSvg += drawRightAngleMarker(
            origineX, origineY, signU * ux, signU * uy, signV * vx, signV * vy, 8,
        );
    }

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VIEWBOX.w} ${VIEWBOX.h}">`
        + `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="#000" stroke-width="1.5" />`
        + `<line x1="${Ax.toFixed(1)}" y1="${Ay.toFixed(1)}" x2="${Bx.toFixed(1)}" y2="${By.toFixed(1)}" stroke="#000" stroke-width="2" stroke-linecap="round" />`
        + `<circle cx="${Ax.toFixed(1)}" cy="${Ay.toFixed(1)}" r="3" fill="#000" />`
        + `<circle cx="${Bx.toFixed(1)}" cy="${By.toFixed(1)}" r="3" fill="#000" />`
        + `<text x="${aLabelX.toFixed(1)}" y="${aLabelY.toFixed(1)}" font-size="14" font-weight="bold">${A_name}</text>`
        + `<text x="${bLabelX.toFixed(1)}" y="${bLabelY.toFixed(1)}" font-size="14" font-weight="bold">${B_name}</text>`
        + `<text x="${dLabelX.toFixed(1)}" y="${dLabelY.toFixed(1)}" font-size="13" font-style="italic" font-family="'Cambria Math','Times New Roman',serif">${subscriptToSvgTspan(d_name)}</text>`
        + codageSvg
        + `</svg>`;
};

const genMediatriceVfReconnaissance = () => {
    const regime = randomChoice(REGIMES);
    const [A, B] = pickN(2);
    const d_name = randomChoice(DROITES);
    const thetaDeg = randomChoice(ORIENTATIONS_DEG);
    const thetaRad = thetaDeg * Math.PI / 180;
    // Minimum 100 px : en dessous, le carré d'angle droit du régime P (décalé de
    // 25 à 35 px du milieu) buterait sur l'extrémité du segment.
    const L = randInt(100, 150);

    // Coordonnées des extrémités du segment (centré sur CENTER)
    const ux = Math.cos(thetaRad);
    const uy = Math.sin(thetaRad);
    const Ax = CENTER.x - (L / 2) * ux;
    const Ay = CENTER.y - (L / 2) * uy;
    const Bx = CENTER.x + (L / 2) * ux;
    const By = CENTER.y + (L / 2) * uy;

    const svg = buildFigureEx1Svg(Ax, Ay, Bx, By, A, B, d_name, regime, thetaRad);
    const imageUrl = `data:image/svg+xml;base64,${svgToBase64(svg)}`;

    // Pools V / F selon le régime
    const poolV = POOL_EX1.filter(a => a.isTrue(regime));
    const poolF = POOL_EX1.filter(a => !a.isTrue(regime));

    // Tirage 3V + 3F distincts
    const chosenV = randomChoiceMultiple(poolV, 3).map(a => a.text(A, B, d_name));
    const chosenF = randomChoiceMultiple(poolF, 3).map(a => a.text(A, B, d_name));

    // SHUFFLE côté générateur — OBLIGATOIRE
    const merged = shuffle([
        ...chosenV.map(l => ({ ligne: l, reponse: 0 })),
        ...chosenF.map(l => ({ ligne: l, reponse: 1 })),
    ]);
    const lignes = merged.map(x => x.ligne);
    const bonnesReponses = merged.map(x => x.reponse);

    const texte = `<p>[${A}${B}] est un segment et ${d_name} est une droite.</p>`;

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, {
        ...opts(L_VF_RECO),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageUrl } }],
        },
    });
    payload._meta.generatorKey = 'mediatrice_vf_reconnaissance';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 2 — Identifier la médiatrice parmi 4 droites (QCM + figure)
// ═══════════════════════════════════════════════════════════

const genMediatriceQcmIdentifierFigure = () => {
    const [A, B] = pickN(2);
    const thetaDeg = randomChoice(ORIENTATIONS_DEG);
    const thetaRad = thetaDeg * Math.PI / 180;
    const L = randInt(100, 150);

    const ux = Math.cos(thetaRad);
    const uy = Math.sin(thetaRad);
    const vx = -uy;
    const vy = ux;

    const Cx = CENTER.x, Cy = CENTER.y;
    const Ax = Cx - (L / 2) * ux;
    const Ay = Cy - (L / 2) * uy;
    const Bx = Cx + (L / 2) * ux;
    const By = Cy + (L / 2) * uy;

    const Ix = Cx, Iy = Cy;

    // Médiatrice (fixe : l'élève doit l'identifier, elle ne change pas de position)
    const dM = { origineX: Ix, origineY: Iy, dirX: vx, dirY: vy, kind: 'M' };

    // Les 3 autres droites sont tirées avec rejection sampling pour garantir
    // une séparation visuelle minimale entre toutes les paires.
    const MIN_SEP_PX = 18;   // distance minimale tolérée entre 2 droites (aux points sondés)
    const PROBE_TS = [-70, -45, -20, 20, 45, 70]; // échantillons le long de l'axe de chaque droite

    // Distance min entre 2 droites, évaluée sur les points sondés de d1 projetés sur d2.
    const pairMinDistance = (d1, d2) => {
        let minD = Infinity;
        for (const t of PROBE_TS) {
            const p1x = d1.origineX + d1.dirX * t;
            const p1y = d1.origineY + d1.dirY * t;
            const lenD2 = Math.hypot(d2.dirX, d2.dirY) || 1;
            const ex = d2.dirX / lenD2;
            const ey = d2.dirY / lenD2;
            const ax = p1x - d2.origineX;
            const ay = p1y - d2.origineY;
            const dot = ax * ex + ay * ey;
            const px = ax - dot * ex;
            const py = ay - dot * ey;
            const d = Math.hypot(px, py);
            if (d < minD) minD = d;
        }
        return minD;
    };

    const tirerTroisAutres = () => {
        const deltaP = randInt(25, 40);
        const signP = randomChoice([-1, 1]);
        const alphaDegL = randomChoice([20, 25, 30]) * randomChoice([-1, 1]);
        const alphaDegQ = randomChoice([20, 30, 45]) * randomChoice([-1, 1]);
        const alphaRadL = alphaDegL * Math.PI / 180;
        const alphaRadQ = alphaDegQ * Math.PI / 180;
        const signQ = randomChoice([-1, 1]);
        const deltaQ = randInt(20, 35);

        return [
            {
                origineX: Ix + signP * deltaP * ux,
                origineY: Iy + signP * deltaP * uy,
                dirX: vx, dirY: vy, kind: 'P',
            },
            {
                origineX: Ix, origineY: Iy,
                dirX: Math.cos(alphaRadL) * vx - Math.sin(alphaRadL) * vy,
                dirY: Math.sin(alphaRadL) * vx + Math.cos(alphaRadL) * vy,
                kind: 'L',
            },
            {
                origineX: Ix + signQ * deltaQ * ux,
                origineY: Iy + signQ * deltaQ * uy,
                dirX: Math.cos(alphaRadQ) * vx - Math.sin(alphaRadQ) * vy,
                dirY: Math.sin(alphaRadQ) * vx + Math.cos(alphaRadQ) * vy,
                kind: 'Q',
            },
        ];
    };

    const toutesSeparees = (droites) => {
        for (let i = 0; i < droites.length; i++) {
            for (let j = i + 1; j < droites.length; j++) {
                if (pairMinDistance(droites[i], droites[j]) < MIN_SEP_PX) return false;
            }
        }
        return true;
    };

    let dP, dL, dQ;
    let attempts = 0;
    do {
        [dP, dL, dQ] = tirerTroisAutres();
        attempts++;
    } while (!toutesSeparees([dM, dP, dL, dQ]) && attempts < 60);
    // Au pire 60 tentatives → si on n'y arrive pas (quasi-impossible vu les plages),
    // on utilise le dernier tirage (acceptable en fallback).

    // Permutation aléatoire : les noms (d₁)..(d₄) sont affectés post-shuffle
    const shuffledDroites = shuffle([dM, dP, dL, dQ]);
    const droiteNames = ['(d₁)', '(d₂)', '(d₃)', '(d₄)'];

    const parts = [];
    // Segment [AB] et extrémités
    parts.push(`<line x1="${Ax.toFixed(1)}" y1="${Ay.toFixed(1)}" x2="${Bx.toFixed(1)}" y2="${By.toFixed(1)}" stroke="#000" stroke-width="2" stroke-linecap="round" />`);
    parts.push(`<circle cx="${Ax.toFixed(1)}" cy="${Ay.toFixed(1)}" r="3" fill="#000" />`);
    parts.push(`<circle cx="${Bx.toFixed(1)}" cy="${By.toFixed(1)}" r="3" fill="#000" />`);
    const aLabelX = Ax - ux * 12 - vx * 6;
    const aLabelY = Ay - uy * 12 - vy * 6 + 4;
    const bLabelX = Bx + ux * 12 + vx * 6;
    const bLabelY = By + uy * 12 + vy * 6 + 4;
    parts.push(`<text x="${aLabelX.toFixed(1)}" y="${aLabelY.toFixed(1)}" font-size="14" font-weight="bold">${A}</text>`);
    parts.push(`<text x="${bLabelX.toFixed(1)}" y="${bLabelY.toFixed(1)}" font-size="14" font-weight="bold">${B}</text>`);

    // 4 droites — PAS de codage médiatrice (l'élève doit identifier)
    let nameOfM = null;
    const labels = [];
    shuffledDroites.forEach((dr, idx) => {
        const name = droiteNames[idx];          // label Unicode (choix QCM + énoncé)
        const couleur = COULEURS_DROITES[idx];
        const { x1, y1, x2, y2 } = extendLineToViewBox(dr.origineX, dr.origineY, dr.dirX, dr.dirY);
        parts.push(`<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="${couleur}" stroke-width="1.6" />`);
        // Position brute du label : extrémité la plus haute de la droite, décalée
        const useFirst = (y1 <= y2);
        labels.push({
            name,
            couleur,
            x: Math.max(4, Math.min(VIEWBOX.w - 32, (useFirst ? x1 : x2) + 3)),
            y: Math.max(14, Math.min(VIEWBOX.h - 4, (useFirst ? y1 : y2) + 14)),
        });
        if (dr.kind === 'M') nameOfM = name;
    });

    // Séparation des étiquettes : deux droites presque parallèles sortent du
    // cadre par des points voisins (MIN_SEP_PX n'est contrôlé qu'au centre de
    // la figure). On empile verticalement celles qui se chevauchent encore.
    const LBL_W = 30;   // largeur approximative de « (d₁) » à 13 px
    const LBL_H = 15;   // hauteur de ligne
    labels
        .slice()
        .sort((a, b) => a.y - b.y)
        .forEach((lbl, i, tries) => {
            for (let k = 0; k < i; k++) {
                const other = tries[k];
                if (Math.abs(lbl.x - other.x) < LBL_W && Math.abs(lbl.y - other.y) < LBL_H) {
                    lbl.y = other.y + LBL_H;
                }
            }
            // Si la pile déborde par le bas, on remonte au-dessus du point de départ
            if (lbl.y > VIEWBOX.h - 4) lbl.y -= LBL_H * (i + 1) + 14;
            lbl.y = Math.max(14, Math.min(VIEWBOX.h - 4, lbl.y));
        });

    labels.forEach((lbl) => {
        parts.push(`<text x="${lbl.x.toFixed(1)}" y="${lbl.y.toFixed(1)}" font-size="13" font-weight="bold" font-style="italic" font-family="'Cambria Math','Times New Roman',serif" fill="${lbl.couleur}">${subscriptToSvgTspan(lbl.name)}</text>`);
    });

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VIEWBOX.w} ${VIEWBOX.h}">${parts.join('')}</svg>`;
    const imageUrl = `data:image/svg+xml;base64,${svgToBase64(svg)}`;

    const choix = ['(d₁)', '(d₂)', '(d₃)', '(d₄)'];
    const texte =
        `<p>Laquelle de ces quatre droites <strong>semble être la médiatrice</strong> du segment [${A}${B}] ?</p>`;

    const payload = makeQcm(choix, nameOfM, texte, {
        ...opts(L_QCM_IDENT),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageUrl } }],
        },
    });
    payload._meta.generatorKey = 'mediatrice_qcm_identifier_figure';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 3 — Définition verbale (QCM sans figure)
// ═══════════════════════════════════════════════════════════

/**
 * Les options ne reprennent pas le préfixe « La médiatrice de [AB] est… » : il
 * est déjà dans la question, et répété quatre fois il ne fait qu'allonger la
 * lecture.
 */
const DEFINITIONS_CORRECTES = [
    (A, B) => `La droite perpendiculaire à (${A}${B}) qui passe par le milieu de [${A}${B}].`,
    (A, B) => `La droite qui coupe [${A}${B}] en son milieu et qui lui est perpendiculaire.`,
];

/**
 * Six IDÉES fausses distinctes, chacune avec une ou deux formulations. On tire
 * trois idées différentes, puis une formulation par idée : deux options ne
 * peuvent donc jamais dire la même chose (l'élève ne peut pas éliminer une
 * paire en bloc), et la longueur des propositions varie d'un tirage à l'autre.
 */
const DEFINITIONS_FAUSSES = [
    // Une seule des deux conditions : perpendiculaire
    [
        (A, B) => `La droite perpendiculaire à (${A}${B}).`,
        (A, B) => `La droite perpendiculaire au segment [${A}${B}], sans autre condition.`,
    ],
    // Une seule des deux conditions : le milieu
    [
        (A, B) => `La droite qui passe par le milieu de [${A}${B}].`,
        (A, B) => `La droite qui coupe le segment [${A}${B}] en deux parties de même longueur.`,
    ],
    // Bonne relation, mauvais point
    [
        (A, B) => `La droite perpendiculaire à (${A}${B}) qui passe par le point ${A}.`,
        (A, B) => `La droite perpendiculaire à (${A}${B}) qui passe par l'extrémité ${A} du segment.`,
    ],
    // Mauvaise relation
    [
        (A, B) => `La droite parallèle à (${A}${B}) qui passe par le milieu de [${A}${B}].`,
        (A, B) => `La droite parallèle à (${A}${B}) qui passe par le milieu du segment [${A}${B}].`,
    ],
    // La droite (AB) elle-même
    [
        (A, B) => `La droite qui passe par les points ${A} et ${B}.`,
        (A, B) => `La droite qui passe par les deux extrémités du segment [${A}${B}].`,
    ],
    // Mauvais objet : un point, pas une droite
    [
        (A, B) => `Le milieu du segment [${A}${B}].`,
        (A, B) => `Le point situé exactement au milieu du segment [${A}${B}].`,
    ],
];

const genMediatriceQcmDefinition = () => {
    const [A, B] = pickN(2);

    const correct = randomChoice(DEFINITIONS_CORRECTES)(A, B);
    const distracteurs = randomChoiceMultiple(DEFINITIONS_FAUSSES, 3)
        .map(formulations => randomChoice(formulations)(A, B));

    const choix = [correct, ...distracteurs];
    const texte = `<p>Quelle est la <strong>définition</strong> de la médiatrice du segment [${A}${B}] ?</p>`;

    const payload = makeQcm(choix, correct, texte, opts(L_QCM_DEF));
    payload._meta.generatorKey = 'mediatrice_qcm_definition';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 4 — Vocabulaire médiatrice (texte à trous)
// ═══════════════════════════════════════════════════════════

/**
 * Distracteurs classés PAR TROU. Chaque bonne réponse a ses propres concurrents,
 * de même nature grammaticale : un adjectif de relation pour `perpendiculaire`,
 * un nom pour `milieu`, un groupe de mots pour `axe de symétrie`, un nombre pour
 * `deux`. Sans ce classement, un trou dont aucune étiquette du même type n'est
 * proposée se remplit sans réfléchir (mesuré à 58 % des tirages).
 */
const DISTRACTEURS_PAR_TROU = {
    perpendiculaire: ['parallèle', 'sécante'],
    milieu: ['côté', 'sommet', 'extrémité', 'centre', 'diagonale', 'diamètre', 'rayon'],
    'axe de symétrie': ['centre de symétrie'],
    deux: ['trois', 'quatre'],
};

/** Réserve commune, pour compléter le tirage une fois chaque trou pourvu. */
const DISTRACTEURS_RESERVE = [...new Set(Object.values(DISTRACTEURS_PAR_TROU).flat())];

/**
 * Pool de 6 modèles de phrases, tous paramétrés par (A, B).
 * Chaque modèle retourne { phrase, bonnes } avec les lettres instanciées.
 */
const MODELES_VOCAB = [
    (A, B) => ({
        phrase: `La médiatrice du segment [${A}${B}] est la droite {{perpendiculaire}} au segment passant par son {{milieu}}.`,
        bonnes: ['perpendiculaire', 'milieu'],
    }),
    (A, B) => ({
        phrase: `La médiatrice du segment [${A}${B}] est la droite {{perpendiculaire}} à [${A}${B}] qui passe par le {{milieu}} du segment.`,
        bonnes: ['perpendiculaire', 'milieu'],
    }),
    (A, B) => ({
        phrase: `Une droite est la médiatrice du segment [${A}${B}] si elle est {{perpendiculaire}} au segment et passe par son {{milieu}}.`,
        bonnes: ['perpendiculaire', 'milieu'],
    }),
    (A, B) => ({
        phrase: `La médiatrice du segment [${A}${B}] est son {{axe de symétrie}} : c'est la droite qui est {{perpendiculaire}} au segment et passe par son {{milieu}}.`,
        bonnes: ['axe de symétrie', 'perpendiculaire', 'milieu'],
    }),
    (A, B) => ({
        phrase: `Pour tracer la médiatrice du segment [${A}${B}], on trace la droite qui est {{perpendiculaire}} à ce segment et qui coupe [${A}${B}] en son {{milieu}}.`,
        bonnes: ['perpendiculaire', 'milieu'],
    }),
    (A, B) => ({
        phrase: `La médiatrice du segment [${A}${B}] vérifie {{deux}} conditions : être {{perpendiculaire}} à [${A}${B}] et passer par le {{milieu}} du segment.`,
        bonnes: ['deux', 'perpendiculaire', 'milieu'],
    }),
];

const genMediatriceVocabulaireTrous = () => {
    const [A, B] = pickN(2);
    const modeleFn = randomChoice(MODELES_VOCAB);
    const { phrase, bonnes } = modeleFn(A, B);

    // 1. Un concurrent garanti pour chaque trou, tiré dans sa propre catégorie.
    const distracteurs = [];
    bonnes.forEach((mot) => {
        const candidats = (DISTRACTEURS_PAR_TROU[mot] || [])
            .filter(d => !bonnes.includes(d) && !distracteurs.includes(d));
        if (candidats.length) distracteurs.push(randomChoice(candidats));
    });

    // 2. Complément au hasard dans la réserve, jusqu'à 3 ou 4 étiquettes en trop.
    const cible = Math.max(distracteurs.length + 1, randInt(3, 4));
    const reste = DISTRACTEURS_RESERVE
        .filter(d => !bonnes.includes(d) && !distracteurs.includes(d));
    const aAjouter = Math.max(0, Math.min(cible - distracteurs.length, reste.length));
    if (aAjouter > 0) distracteurs.push(...randomChoiceMultiple(reste, aAjouter));

    // Texte OBLIGATOIRE (le serializer back refuse un énoncé vide au POST —
    // revue 2026-08-11) : une consigne d'une ligne, la phrase à trous reste
    // le coeur du format.
    const texte = '<p>Complète la phrase en glissant les étiquettes.</p>';

    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_VOCAB),
        mode: 'drag',
        distracteurs: shuffle(distracteurs),
    });
    payload._meta.generatorKey = 'mediatrice_vocabulaire_trous';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 5 — Notations équivalentes symétrie ↔ médiatrice (V/F sans figure)
// ═══════════════════════════════════════════════════════════

const POOL_V_EX5 = [
    (A, B, d) => `${B} est le symétrique de ${A} par rapport à ${d}.`,
    (A, B, d) => `${A} est le symétrique de ${B} par rapport à ${d}.`,
    (A, B, d) => `Les points ${A} et ${B} sont symétriques par rapport à ${d}.`,
    (A, B, d) => `${d} est un axe de symétrie du segment [${A}${B}].`,
    (A, B, d) => `${B} est l'image de ${A} par la symétrie d'axe ${d}.`,
    (A, B, d) => `La droite ${d} est perpendiculaire à (${A}${B}) et passe par le milieu de [${A}${B}].`,
];

const POOL_F_EX5 = [
    (A, B, d) => `La droite ${d} passe par les points ${A} et ${B}.`,
    (A, B, d) => `${A} est le symétrique de ${A} par rapport à ${d}.`,
    (A, B, d) => `La droite ${d} est parallèle à la droite (${A}${B}).`,
    (A, B, d) => `La droite (${A}${B}) est la médiatrice du segment [${A}${B}].`,
    (A, B, d) => `La droite ${d} passe par le milieu de [${A}${B}] mais n'est pas perpendiculaire à (${A}${B}).`,
    (A, B, d) => `La droite ${d} est perpendiculaire à (${A}${B}) mais ne passe pas par le milieu de [${A}${B}].`,
];

const genMediatriceNotationsVf = () => {
    const [A, B] = pickN(2);
    const d = randomChoice(DROITES);

    const chosenV = randomChoiceMultiple(POOL_V_EX5, 3).map(fn => fn(A, B, d));
    const chosenF = randomChoiceMultiple(POOL_F_EX5, 3).map(fn => fn(A, B, d));

    // SHUFFLE côté générateur — OBLIGATOIRE
    const merged = shuffle([
        ...chosenV.map(l => ({ ligne: l, reponse: 0 })),
        ...chosenF.map(l => ({ ligne: l, reponse: 1 })),
    ]);
    const lignes = merged.map(x => x.ligne);
    const bonnesReponses = merged.map(x => x.reponse);

    const texte = `<p>On suppose que la droite ${d} est la médiatrice du segment [${A}${B}].</p>`;

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_NOTATIONS));
    payload._meta.generatorKey = 'mediatrice_notations_vf';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    mediatrice_vf_reconnaissance:     genMediatriceVfReconnaissance,
    mediatrice_qcm_identifier_figure: genMediatriceQcmIdentifierFigure,
    mediatrice_qcm_definition:        genMediatriceQcmDefinition,
    mediatrice_vocabulaire_trous:     genMediatriceVocabulaireTrous,
    mediatrice_notations_vf:          genMediatriceNotationsVf,
};

export const CATEGORIES = [
    { id: 'mediatrice_vf_reco',        label: L_VF_RECO,   formatsList: [{ key: 'mediatrice_vf_reconnaissance',       label: 'Vrai/Faux' }] },
    { id: 'mediatrice_qcm_identifier', label: L_QCM_IDENT, formatsList: [{ key: 'mediatrice_qcm_identifier_figure',   label: 'QCM' }] },
    { id: 'mediatrice_qcm_def',        label: L_QCM_DEF,   formatsList: [{ key: 'mediatrice_qcm_definition',          label: 'QCM' }] },
    { id: 'mediatrice_vocab',          label: L_VOCAB,     formatsList: [{ key: 'mediatrice_vocabulaire_trous',       label: 'Texte à trous' }] },
    { id: 'mediatrice_notations',      label: L_NOTATIONS, formatsList: [{ key: 'mediatrice_notations_vf',            label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genMediatriceVfReconnaissance,
    genMediatriceQcmIdentifierFigure,
    genMediatriceQcmDefinition,
    genMediatriceVocabulaireTrous,
    genMediatriceNotationsVf,
};
